import { LessonDTO } from '@/components/Common/Modals/ScheduleLessonModal/types';
import toast from 'react-hot-toast';
import { z } from 'zod';

// Debounce utility function
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

// Zod schema for profile validation
export const profileSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(15, 'Username must be at most 15 characters'),
  name: z.string().min(1, 'Name is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  contact_number: z.string().min(1, 'Phone is required'),
  address: z.string().min(1, 'Address is required'),
  avatar: z.string().optional()
});

// Generic Zod validation function for forms
export function validateWithSchema<T extends object>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: boolean; errors: Partial<Record<keyof T, string>> } {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, errors: {} };
  }
  const errors: Partial<Record<keyof T, string>> = {};
  for (const err of result.error.errors) {
    if (err.path[0]) errors[err.path[0] as keyof T] = err.message;
  }
  return { success: false, errors };
}

// Utility for avatar preview - DEPRECATED
// Use the FormImageUpload component instead which handles this safely
export function previewAvatar(file: File, imgId: string) {
  // This function is deprecated and should not be used in new code
  const url = URL.createObjectURL(file);
  const img = document.getElementById(imgId) as HTMLImageElement;
  if (img) {
    img.src = url;
    // Cleanup previous URL to prevent memory leaks
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }
}

export const uploadMediaToS3 = async ({
  url,
  mediaType,
  mediaFile
}: {
  url: string;
  mediaType: string;
  mediaFile: File;
}): Promise<Response> => {
  const headers = new Headers({ 'Content-Type': mediaType });

  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: mediaFile
    });

    if (!response.ok) {
      toast.dismiss();
      toast.error(`Upload failed with status ${response.status}`);
      throw new Error('Upload failed');
    }

    return response;
  } catch (error) {
    // Log error without exposing sensitive information
    toast.dismiss();
    toast.error('Upload failed. Please try again.');
    throw error;
  }
};

/**
 * Format date in local timezone as YYYY-MM-DD
 * @param date - Date object to format
 * @returns Formatted date string
 */
export const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse date string and create Date object in local timezone
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object in local time
 */
export const parseDateLocal = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Get current date in local timezone as YYYY-MM-DD
 * @returns Current date string
 */
export const getCurrentDateLocal = (): string => {
  return formatDateLocal(new Date());
};

/**
 * Check if a date string is valid
 * @param dateString - Date string to validate
 * @returns True if valid date string
 */
export const isValidDateString = (dateString: string): boolean => {
  const date = parseDateLocal(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Add days to a date string
 * @param dateString - Starting date string
 * @param days - Number of days to add (can be negative)
 * @returns New date string
 */
export const addDaysToDate = (dateString: string, days: number): string => {
  const date = parseDateLocal(dateString);
  date.setDate(date.getDate() + days);
  return formatDateLocal(date);
};

/**
 * Get date difference in days
 * @param dateString1 - First date string
 * @param dateString2 - Second date string
 * @returns Difference in days
 */
export const getDateDifferenceInDays = (
  dateString1: string,
  dateString2: string
): number => {
  const date1 = parseDateLocal(dateString1);
  const date2 = parseDateLocal(dateString2);
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Check if a date is today
 * @param dateString - Date string to check
 * @returns True if date is today
 */
export const isToday = (dateString: string): boolean => {
  return dateString === getCurrentDateLocal();
};

// src/common/utils/color.ts

/**
 * Convert HEX | RGB | RGBA string to RGBA with given opacity.
 * If invalid input → falls back to white.
 */
export const convertToRgba = (color: string, opacity: number = 1): string => {
  try {
    // ✅ Already RGBA → replace alpha
    if (color.startsWith('rgba')) {
      return color.replace(
        /rgba\(([^,]+),([^,]+),([^,]+),([^)]+)\)/,
        `rgba($1,$2,$3,${opacity})`
      );
    }

    // ✅ Already RGB → add alpha
    if (color.startsWith('rgb')) {
      return color.replace(
        /rgb\(([^,]+),([^,]+),([^,]+)\)/,
        `rgba($1,$2,$3,${opacity})`
      );
    }

    // ✅ HEX (#fff or #ffffff)
    let c = color.replace('#', '');
    if (c.length === 3) {
      c = c
        .split('')
        .map(ch => ch + ch)
        .join('');
    }

    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);

    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      throw new Error('Invalid hex color');
    }

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } catch {
    // ✅ Fallback (invalid color string)
    return `rgba(255, 255, 255, ${opacity})`;
  }
};

export const toHHmm = (time?: string | number | Date | null): string => {
  if (time == null) return '';

  // Date instance
  if (time instanceof Date) {
    if (!isNaN(time.getTime())) {
      const hh = String(time.getHours()).padStart(2, '0');
      const mm = String(time.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }
    return '';
  }

  // Numeric timestamp
  if (typeof time === 'number') {
    const d = new Date(time);
    if (!isNaN(d.getTime())) {
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }
    return '';
  }

  // String input
  if (typeof time === 'string') {
    if (time.includes('T')) {
      const d = new Date(time);
      if (!isNaN(d.getTime())) {
        // Use UTC to avoid timezone shifts when backend provides ISO times in Z
        const hh = String(d.getUTCHours()).padStart(2, '0');
        const mm = String(d.getUTCMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      }
      return '';
    }
    // Assume already HH:mm
    return time;
  }

  return '';
};

export const toYyyyMmDd = (time?: string) => {
  if (!time) return '';
  if (typeof time === 'string') {
    // Robustly extract YYYY-MM-DD if present at start (handles T, space, or end of string)
    // This avoids timezone shifts that occur with new Date() on ISO strings
    const match = time.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
      return match[1];
    }

    // Fallback for non-standard formats (e.g. MM/DD/YYYY) to maintain compatibility
    const d = new Date(time);
    if (!isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  return time;
};

export const getMimeType = (type: string): string => {
  const normalizedType = type.toLowerCase();

  const mimeMap: Record<string, string> = {
    pdf: 'application/pdf',
    'summary-pdf': 'application/pdf',
    excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'summary-excel':
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv'
  };

  return mimeMap[normalizedType] || 'application/octet-stream';
};

export const downloadReportFile = async (
  blob: Blob,
  payload: string,
  filename?: string
) => {
  const params = new URLSearchParams(payload);
  const type = params.get('type') || params.get('export_type') || 'excel';
  const mimeType = getMimeType(type);
  const extension = type.includes('pdf') ? 'pdf' : 'xlsx';
  const resolvedFilename = `${filename || 'report'}.${extension}`;
  const fileBlob = new Blob([blob], { type: mimeType });

  /**
   * 🖨️ Handle print case
   */

  if (type === 'print') {
    const blobUrl = URL.createObjectURL(blob);
    // open in new tab
    const printWindow = window.open(blobUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print(); // triggers the browser print dialog (Ctrl+P)
      };
    } else {
      alert('Popup blocked — please allow popups to view/print the report.');
    }

    return;
  }

  /**
   * 💾 Try using File System Access API
   */
  if ('showSaveFilePicker' in window) {
    const saved = await trySaveFileWithPicker(
      fileBlob,
      mimeType,
      resolvedFilename,
      extension
    );
    if (saved) return; // ✅ Stop if file saved or user canceled
  }
  triggerBrowserDownload(fileBlob, resolvedFilename);
};

const trySaveFileWithPicker = async (
  fileBlob: Blob,
  mimeType: string,
  filename: string,
  extension: string
): Promise<boolean> => {
  try {
    const fileHandle = await (window as any).showSaveFilePicker({
      suggestedName: filename,
      types: [
        {
          description: 'Report File',
          accept: { [mimeType]: [`.${extension}`] }
        }
      ]
    });

    const writable = await fileHandle.createWritable();
    await writable.write(fileBlob);
    await writable.close();

    console.info('✅ File saved successfully.');
    return true;
  } catch (err: unknown) {
    const error = err as { name?: string; message?: string };
    if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
      console.info('Save canceled by user — no download triggered.');
      return true; // ✅ Treated as handled, so don’t fallback
    }

    console.warn('Save API failed, will fallback to download:', err);
    return false; // ❌ Trigger fallback
  }
};

//
// 🔹 Helper: Fallback download via anchor link
//
const triggerBrowserDownload = (fileBlob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(fileBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
  console.info('⬇️ File downloaded using fallback method.');
};

export const getAllDatesInMonth = (dateString: string) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based (0 = January)

  // Get total days in month — handles leap years automatically
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Generate array of all dates in YYYY-MM-DD format
  const dates = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  });

  return dates;
};

export const groupBookingsByDate = (
  bookings: Array<{ start?: string; string?: string }>
): Record<string, { start?: string; string?: string }[]> => {
  const toDateKey = (value?: string): string | null => {
    if (!value) return null;
    // Accept ISO strings (contain 'T') or plain YYYY-MM-DD
    if (value.includes('T')) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
      return null;
    }
    // Basic YYYY-MM-DD check
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    return null;
  };

  const list = Array.isArray(bookings) ? bookings : [];
  return list.reduce(
    (acc: Record<string, { start?: string; string?: string }[]>, booking) => {
      // Prefer valid date from start; fall back to a valid string date
      const keyFromStart = toDateKey(booking.start);
      const keyFromString = toDateKey(booking.string);
      const dateKey = keyFromStart || keyFromString;
      if (!dateKey) return acc;
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(booking);
      return acc;
    },
    {}
  );
};

export const createUTCDate = (dateString: string) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
};

// Helper function to split comma-separated values
export const splitInstructorIds = (
  value: string | null | undefined
): string[] => {
  // Handle null, undefined, empty string
  if (!value || typeof value !== 'string' || value.trim() === '') return [];

  // Split, trim, and filter out invalid values
  return value
    .split(',')
    .map(v => v.trim())
    .filter(v => v.length > 0 && !isNaN(Number(v)));
};

export function removeLessonById<
  Instructor extends { lessons?: Lesson[] },
  Lesson extends { id?: number | string }
>(instructors: Instructor[], lessonId: number | string): Instructor[] {
  return instructors?.map(instructor => ({
    ...instructor,
    lessons:
      instructor?.lessons?.filter(
        lesson => String(lesson?.id) !== String(lessonId)
      ) || []
  }));
}

export function removeLessonByScheduleGroupId<
  Item extends { lessonCodes?: Lesson[] },
  Lesson extends { schedule_group_id?: number | string }
>(items: Item[], scheduleGroupId: number | string): Item[] {
  return items?.map(item => ({
    ...item,
    lessonCodes:
      item?.lessonCodes?.filter(
        lesson => String(lesson?.schedule_group_id) !== String(scheduleGroupId)
      ) || []
  }));
}

export function removeInstructorLessonById(
  lessons: LessonDTO[],
  lessonId: number | string
) {
  return (
    lessons?.filter(lesson => String(lesson?.id) !== String(lessonId)) || []
  );
}

export function appendLessonToInstructor<
  Instructor extends { id?: number; lessons?: Lesson[] },
  Lesson
>(
  instructors: Instructor[] | undefined,
  instructorId: number,
  newLesson: Lesson
): Instructor[] | undefined {
  if (!Array.isArray(instructors)) return instructors;
  return instructors.map(instructor => {
    if (instructor?.id === instructorId) {
      return {
        ...instructor,
        lessons: [...(instructor?.lessons || []), newLesson]
      } as Instructor;
    }
    return instructor;
  });
}

type LessonCode = {
  id: number | string;
  code?: string;
  [key: string]: any;
};

type DateItem = {
  date: string; // formatted as yyyy-mm-dd
  lessonCodes?: LessonCode[];
};

export const addLessonToDate = (
  dates: DateItem[],
  rawData: any // your full object
): DateItem[] => {
  const dateKey = toYyyyMmDd(rawData?.start); // extract yyyy-mm-dd
  if (!dateKey || !rawData?.lesson_code) return dates;

  const lessonCode = {
    ...rawData.lesson_code,
    schedule_group_id: rawData.schedule_group?.id
  }; // pick only lesson_code object
  // Check if date already exists
  const existingIndex = dates.findIndex(d => d.date === dateKey);

  if (existingIndex >= 0) {
    // Add lesson to existing date
    const dateEntry = dates[existingIndex];
    const existingLessons = dateEntry.lessonCodes || [];

    // Avoid duplicates by id
    const alreadyExists = existingLessons.some(l => l.id === lessonCode.id);
    if (alreadyExists) return dates;

    const updatedEntry = {
      ...dateEntry,
      lessonCodes: [...existingLessons, lessonCode]
    };

    return [
      ...dates.slice(0, existingIndex),
      updatedEntry,
      ...dates.slice(existingIndex + 1)
    ];
  }

  return [
    ...dates,
    {
      date: dateKey,
      lessonCodes: [lessonCode]
    }
  ];
};

export const getDayNumber = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDay();
  return day === 0 ? 7 : day;
};
